import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Section from '../section';
import Button from '../UI/button';
import { ExecuteResponse, PaymentStatus as PaymentStatusEnum } from '../../api/payamigo';
import { ReactComponent as CopyIcon } from '../../assets/icons/icon-copy.svg';
import './payment-status.scss';

interface IPaymentStatusProps {
  currentStatus: PaymentStatusEnum;
  executeResult: ExecuteResponse;
  isPaymentComplete: boolean;
  isPaymentFailed: boolean;
  onClose?: () => void;
}

const PaymentStatus: React.FC<IPaymentStatusProps> = ({
  currentStatus,
  executeResult,
  isPaymentComplete,
  isPaymentFailed,
  onClose,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopyTransactionId = () => {
    navigator.clipboard.writeText(executeResult.transaction_id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Render icon based on status
  const renderIcon = () => {
    if (currentStatus === PaymentStatusEnum.CONVERSION_COMPLETED) {
      return (
        <div className="status-icon success">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="40" cy="40" r="40" fill="#10B981" fillOpacity="0.1" />
            <circle cx="40" cy="40" r="32" fill="#10B981" />
            <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    }

    if (currentStatus === PaymentStatusEnum.PAYMENT_COMPLETED) {
      return (
        <div className="status-icon processing">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="40" cy="40" r="40" fill="#F59E0B" fillOpacity="0.1" />
            <circle cx="40" cy="40" r="32" fill="#F59E0B" />
            <path d="M40 24v16m0 8v0" stroke="white" strokeWidth="4" strokeLinecap="round"/>
            <circle cx="40" cy="56" r="2" fill="white"/>
          </svg>
        </div>
      );
    }

    if (isPaymentFailed) {
      return (
        <div className="status-icon error">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="40" cy="40" r="40" fill="#EF4444" fillOpacity="0.1" />
            <path d="M40 10 L70 65 L10 65 Z" fill="#EF4444" />
            <path d="M40 30v15" stroke="white" strokeWidth="4" strokeLinecap="round"/>
            <circle cx="40" cy="52" r="2.5" fill="white"/>
          </svg>
        </div>
      );
    }

    return (
      <div className="status-icon pending">
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="30" cy="30" r="30" fill="rgba(0, 123, 255, 0.1)" />
          <circle cx="30" cy="30" r="24" fill="#007BFF" />
          <path d="M30 18v12l8 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    );
  };

  // Get title based on status
  const getTitle = () => {
    if (currentStatus === PaymentStatusEnum.CONVERSION_COMPLETED) {
      return t('PAYMENT_SUCCESS.TITLE', { defaultValue: 'Payment Successful!' });
    }
    if (currentStatus === PaymentStatusEnum.PAYMENT_COMPLETED) {
      return t('PAYMENT_ACCEPTED.TITLE', { defaultValue: 'Payment Accepted' });
    }
    return t('PAY_AMIGO.STATUS_TITLE', { defaultValue: 'Payment Status' });
  };

  // Get message based on status
  const getMessage = () => {
    if (currentStatus === PaymentStatusEnum.CONVERSION_COMPLETED) {
      return t('PAYMENT_SUCCESS.MESSAGE', { defaultValue: 'Your payment has been processed successfully!' });
    }
    if (currentStatus === PaymentStatusEnum.PAYMENT_COMPLETED) {
      return t('PAYMENT_ACCEPTED.MESSAGE', { defaultValue: 'Your payment has been accepted and is being processed. Please wait for the conversion to complete.' });
    }
    if (isPaymentFailed) {
      return t('PAY_AMIGO.PAYMENT_FAILED', { defaultValue: 'Payment failed. Please try again.' });
    }
    return t('PAY_AMIGO.PAYMENT_PENDING', { defaultValue: 'Waiting for payment confirmation...' });
  };

  return (
    <Section
      className="payment-status-section"
      title={getTitle()}
    >
      <div className="payment-status">
        {renderIcon()}

        <p className="status-message">
          {getMessage()}
        </p>

        <div className="payment-status-details">
          {'underlying_amount' in executeResult && executeResult.underlying_amount && executeResult.underlying_coin && (
            <div className="detail-item">
              <span className="label">
                {t('PAY_AMIGO.AMOUNT', { defaultValue: 'Amount' })}
              </span>
              <span className="value">{`${executeResult.underlying_coin} ${executeResult.underlying_amount.toFixed(2)}`}</span>
            </div>
          )}

          {executeResult.quoted_amount !== undefined && executeResult.quoted_amount > 0 && (
            <div className="detail-item">
              <span className="label">
                {t('PAY_AMIGO.QUOTED_AMOUNT', { defaultValue: 'Quoted Amount' })}
              </span>
              <span className="value">{`${executeResult.quoted_amount} ${executeResult.quoted_coin}`}</span>
            </div>
          )}

          {executeResult.quoted_fee !== undefined && executeResult.quoted_fee >= 0 && (
            <div className="detail-item">
              <span className="label">
                {t('PAY_AMIGO.QUOTED_FEE', { defaultValue: 'Quoted Fee' })}
              </span>
              <span className="value">{`${executeResult.quoted_fee} ${executeResult.quoted_coin}`}</span>
            </div>
          )}

          <div className="detail-item">
            <span className="label">
              {t('PAY_AMIGO.TRANSACTION_ID', { defaultValue: 'Transaction ID' })}
            </span>
            <div className="transaction-id-value">
              <span className="value transaction-id" title={executeResult.transaction_id}>
                {executeResult.transaction_id}
              </span>
              <button 
                className={`copy-button ${copied ? 'copied' : ''}`}
                onClick={handleCopyTransactionId}
                title={t('PAY_AMIGO.COPY', { defaultValue: 'Copy' })}
              >
                {copied ? (
                  <span className="check-icon">✓</span>
                ) : (
                  <CopyIcon className="copy-icon"/>
                )}
              </button>
            </div>
          </div>
        </div>

        {currentStatus === PaymentStatusEnum.PAYMENT_COMPLETED && (
          <div className="processing-info">
            <p>
              {t('PAYMENT_ACCEPTED.INFO', {
                defaultValue: 'Please wait while we process your payment. This may take a few moments.',
              })}
            </p>
          </div>
        )}

        {currentStatus === PaymentStatusEnum.CONVERSION_COMPLETED && (
          <div className="success-info">
            <p>
              {t('PAYMENT_SUCCESS.INFO', {
                defaultValue: 'The crypto will be deposited to your wallet shortly.',
              })}
            </p>
          </div>
        )}

        {onClose && currentStatus === PaymentStatusEnum.CONVERSION_COMPLETED && (
          <Button
            className="close-button"
            title={t('PAYMENT_SUCCESS.CLOSE', { defaultValue: 'Done' })}
            onClick={onClose}
          />
        )}
      </div>
    </Section>
  );
};

export default PaymentStatus;

